import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useWebSocket } from '../context/WebSocketContext'
import { useAnnouncement } from '../context/AnnouncementContext'
import { gamesApi } from '../api/games'
import { turnsApi } from '../api/turns'
import Die from '../game/Die'

const CATEGORY_NAMES = {
    1: 'Pairs', 2: 'Triples', 3: 'Evens', 4: 'Odds',
    5: 'Lows', 6: 'Highs', 7: 'Straight',
    8: '3 of a Kind', 9: '4 of a Kind', 10: '5 of a Kind',
    11: '6 of a Kind', 12: '7 of a Kind', 13: 'Spare Change',
}

const CATEGORY_HINTS = {
    1:  "3 pairs of any values (or 4 of a kind + a pair) — red die must be in one of the pairs",
    2:  "2 triples of any values — red die must be in one of the triples",
    3:  "6+ dice showing even numbers (2, 4, 6) — red die must show even",
    4:  "6+ dice showing odd numbers (1, 3, 5) — red die must show odd",
    5:  "6+ dice showing 1, 2, or 3 — red die must show 1, 2, or 3",
    6:  "6+ dice showing 4, 5, or 6 — red die must show 4, 5, or 6",
    7:  "All 6 values (1–6) appear at least once across the 7 dice",
    8:  "3+ of the same value — red die must show that value",
    9:  "4+ of the same value — red die must show that value",
    10: "5+ of the same value — red die must show that value",
    11: "6+ of the same value — red die must show that value",
    12: "All 7 dice show the same value",
    13: "Always scores — total is the sum of all 7 dice",
}

export default function GamePlay() {
    const { gameId } = useParams()
    const navigate   = useNavigate()
    const { user }   = useAuth()
    const { addToast } = useToast()
    const { lastMessage } = useWebSocket()
    const { announceAffirmation } = useAnnouncement()
    const affirmationTimer = React.useRef(null)
    const longPressTimer      = React.useRef(null)
    const contextMenuOpenedAt = React.useRef(0)

    const [contextMenu, setContextMenu] = React.useState(null) // { catId, x, y }

    const [game,           setGame]           = React.useState(null)
    const [turnState,      setTurnState]      = React.useState(null)
    const [loading,        setLoading]        = React.useState(true)
    const [acting,         setActing]         = React.useState(false)
    const [isRolling,      setIsRolling]      = React.useState(false)
    const [gameOver,       setGameOver]       = React.useState(null) // { scores: {playerId: score} }
    const [finalScores,    setFinalScores]    = React.useState(null) // resolved scores for game over screen
    const [showScoresheet, setShowScoresheet] = React.useState(false)
    const [scoresheet,     setScoresheet]     = React.useState(null)

    const id = parseInt(gameId, 10)

    async function loadGame() {
        try {
            const [data, ts] = await Promise.all([
                gamesApi.getGame(id),
                turnsApi.getState(id).catch(() => null),
            ])
            setGame(data)
            if (ts) setTurnState(ts)
        } catch (err) {
            addToast(err.message, 'error')
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => { loadGame() }, []) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!lastMessage) return
        const { event, game_id, scores } = lastMessage

        if (event === 'ws_reconnected') { loadGame(); return }
        if (game_id !== id) return

        if (event === 'your_turn') {
            // Optimistically enable the Roll button before the API round-trip completes.
            setGame(prev => prev ? { ...prev, current_player_id: user.id, current_round: lastMessage.round } : prev)
            if (lastMessage.rolls_remaining != null) {
                setTurnState(prev => prev ? {
                    ...prev,
                    rolls_remaining:  lastMessage.rolls_remaining,
                    dice:             [],
                    met_categories:   {},
                    forced_selection: false,
                    round_num:        lastMessage.round,
                } : {
                    rolls_remaining:  lastMessage.rolls_remaining,
                    dice:             [],
                    met_categories:   {},
                    filled_categories:{},
                    forced_selection: false,
                    round_num:        lastMessage.round,
                    banked_rolls:     0,
                })
            }
            loadGame()
        } else if (event === 'turn_updated') {
            setTurnState(prev => prev ? {
                ...prev,
                dice:             lastMessage.dice,
                rolls_remaining:  lastMessage.rolls_remaining,
                met_categories:   lastMessage.met_categories,
                forced_selection: lastMessage.forced_selection ?? false,
            } : prev)
        } else if (event === 'game_over') {
            setGameOver({ scores })
            gamesApi.getGame(id).then(setGame).catch(() => {})
            affirmationTimer.current = setTimeout(announceAffirmation, 500)
        }
    }, [lastMessage]) // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve final scores for the game over screen.
    // Prefer the scores from the game_over WebSocket event; fall back to the
    // scoresheet API if the event arrived before/after game.status flipped.
    React.useEffect(() => {
        const isOver = gameOver || (game?.status === 'complete')
        if (!isOver || finalScores) return

        const wsScores = gameOver?.scores
        if (wsScores && Object.keys(wsScores).length > 0) {
            const parsed = Object.fromEntries(
                Object.entries(wsScores).map(([k, v]) => [parseInt(k, 10), v])
            )
            setFinalScores(parsed)
            return
        }

        turnsApi.getScoresheet(id)
            .then(data => {
                const totals = {}
                for (const [pidStr, cats] of Object.entries(data.scores)) {
                    totals[parseInt(pidStr, 10)] = Object.values(cats).reduce((s, v) => s + v, 0)
                }
                setFinalScores(totals)
            })
            .catch(() => {})
    }, [gameOver, game?.status]) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        return () => { if (affirmationTimer.current) clearTimeout(affirmationTimer.current) }
    }, [])

    // Safety-net poll: if a WebSocket message is dropped both players get stuck.
    React.useEffect(() => {
        if (!game || game.status !== 'active') return
        const interval = setInterval(loadGame, 3000)
        return () => clearInterval(interval)
    }, [game?.status]) // eslint-disable-line react-hooks/exhaustive-deps

    // Reload when the user switches back to the tab.
    React.useEffect(() => {
        function onVisible() { if (document.visibilityState === 'visible') loadGame() }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    async function act(fn) {
        if (acting) return
        setActing(true)
        try {
            const ts = await fn()
            setTurnState(ts)
            const data = await gamesApi.getGame(id)
            setGame(data)
        } catch (err) {
            addToast(err.message, 'error')
        } finally {
            setActing(false)
        }
    }

    async function openScoresheet() {
        try {
            const data = await turnsApi.getScoresheet(id)
            const parsed = {}
            for (const [pidStr, cats] of Object.entries(data.scores)) {
                parsed[parseInt(pidStr, 10)] = Object.fromEntries(
                    Object.entries(cats).map(([cidStr, score]) => [parseInt(cidStr, 10), score])
                )
            }
            setScoresheet({ ...data, scores: parsed })
            setShowScoresheet(true)
        } catch (err) {
            addToast(err.message, 'error')
        }
    }

    function openContextMenu(catId, x, y) {
        contextMenuOpenedAt.current = Date.now()
        const adjusted = {
            catId,
            x: Math.min(x + 4, window.innerWidth  - 232),
            y: Math.min(y + 4, window.innerHeight - 100),
        }
        setContextMenu(adjusted)
    }

    function handleCategoryContextMenu(e, catId) {
        e.preventDefault()
        openContextMenu(catId, e.clientX, e.clientY)
    }

    function handleCategoryTouchStart(e, catId) {
        const touch = e.touches[0]
        const x = touch.clientX
        const y = touch.clientY
        longPressTimer.current = setTimeout(() => openContextMenu(catId, x, y), 500)
    }

    function cancelLongPress() {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }

    React.useEffect(() => {
        if (!contextMenu) return
        function dismiss() {
            if (Date.now() - contextMenuOpenedAt.current < 400) return
            setContextMenu(null)
        }
        document.addEventListener('click', dismiss)
        document.addEventListener('touchstart', dismiss)
        return () => {
            document.removeEventListener('click', dismiss)
            document.removeEventListener('touchstart', dismiss)
        }
    }, [contextMenu])

    React.useEffect(() => {
        return () => cancelLongPress()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const isMyTurn = game && user && game.current_player_id === user.id

    if (loading) {
        return (
            <main className="flex min-h-screen w-full items-center justify-center bg-bankroll-teal">
                <p className="text-gray-100">Loading…</p>
            </main>
        )
    }

    if (gameOver || (game && game.status === 'complete')) {
        const scores  = finalScores ?? {}
        const players = game?.players ?? []
        const sorted  = [...players].sort((a, b) => (scores[b.player_id] ?? 0) - (scores[a.player_id] ?? 0))

        return (
            <div className="min-h-screen w-full bg-bankroll-teal">
            <main className="page-main">
                <h2 className="text-2xl font-bold text-bankroll-header m-0">Game Over</h2>

                <section className="bg-white rounded-xl p-4 shadow-sm">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3 m-0">Final Scores</h3>
                    <ul className="list-none m-0 p-0 flex flex-col gap-2">
                        {sorted.map((p, i) => (
                            <li key={p.player_id} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {i === 0 && <span className="text-yellow-500 font-bold">★</span>}
                                    <span className={`font-bold text-bankroll-text ${p.player_id === user?.id ? 'underline' : ''}`}>
                                        {p.username}
                                    </span>
                                </div>
                                <span className="font-bold text-bankroll-primary">{scores[p.player_id] ?? '—'}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <button
                    className="self-start px-5 py-2.5 bg-bankroll-primary text-white border-0 rounded-md font-karla font-bold cursor-pointer hover:opacity-90"
                    onClick={() => navigate('/lobby')}
                >
                    Back to Lobby
                </button>
            </main>
            </div>
        )
    }

    const currentPlayer = game?.players?.find(p => p.player_id === game?.current_player_id)
    const myPlayer      = game?.players?.find(p => p.player_id === user?.id)

    const dice              = turnState?.dice ?? []
    const rollsRemaining    = turnState?.rolls_remaining ?? 0
    const bankedRolls       = turnState?.banked_rolls ?? 0
    const forcedSelection   = turnState?.forced_selection ?? false
    const metCategories    = Object.fromEntries(Object.entries(turnState?.met_categories ?? {}).map(([k,v]) => [parseInt(k,10), v]))
    const filledCategories = Object.fromEntries(Object.entries(turnState?.filled_categories ?? {}).map(([k,v]) => [parseInt(k,10), v]))

    const totalScore = Object.values(filledCategories).reduce((sum, v) => sum + v, 0)

    const canRoll  = isMyTurn && rollsRemaining > 0
    const canBank  = isMyTurn && rollsRemaining > 0 && Object.keys(metCategories).length === 0 && turnState?.round_num < 13
    const canScore = isMyTurn && Object.keys(metCategories).length > 0

    return (
        <>
        <div className="min-h-screen w-full bg-bankroll-teal">
        <main className="page-main">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-100 m-0">
                    Round {turnState?.round_num ?? game?.current_round ?? '—'} / 13
                </h2>
                <div className="flex flex-col items-end gap-1 text-xs font-bold text-bankroll-on-teal uppercase tracking-wide">
                    <div className="flex gap-2">
                        <span>Rolls left: <span className="text-bankroll-on-teal">{rollsRemaining}</span></span>
                        <span className="ml-2">Banked: <span className="text-bankroll-on-teal">{bankedRolls}</span></span>
                    </div>
                    <span>Total Score: <span className="text-bankroll-on-teal">{totalScore}</span></span>
                </div>
            </div>

            {/* Turn indicator */}
            {isMyTurn ? (
                <div className={`border rounded-lg px-4 py-2.5 ${forcedSelection ? 'bg-red-900 border-red-500' : 'bg-bankroll-lavender border-bankroll-primary'}`}>
                    <p className={`font-bold text-sm m-0 ${forcedSelection ? 'text-white' : 'text-bankroll-text'}`}>
                        {forcedSelection
                            ? 'No rolls left and no categories met — select a category to score zero and continue.'
                            : 'Your turn — roll the dice!'}
                    </p>
                </div>
            ) : (
                <div className="bg-gray-800 rounded-lg px-4 py-2.5 flex items-center justify-between">
                    <p className="text-gray-400 text-sm m-0">
                        Live — watching <span className="text-gray-200 font-bold">{currentPlayer?.username ?? '…'}</span> roll
                    </p>
                    <span className="text-xs text-gray-500 font-bold">{rollsRemaining} roll{rollsRemaining !== 1 ? 's' : ''} left</span>
                </div>
            )}

            {/* Dice */}
            <section>
                <div className="flex gap-2.5 flex-wrap">
                    {dice.length === 0 ? (
                        Array.from({ length: 7 }).map((_, i) => (
                            <Die key={i} value={1} isHeld={false} isRed={i === 0} />
                        ))
                    ) : dice.map(d => (
                        <Die
                            key={d.id}
                            value={d.value}
                            isHeld={d.is_held}
                            isRed={d.is_red}
                            isRolling={isRolling && !d.is_held}
                            holdDice={isMyTurn ? () => act(() => turnsApi.holdDie(id, d.id)) : undefined}
                        />
                    ))}
                </div>
                {dice.length > 0 && isMyTurn && (
                    <p className="text-xs text-gray-500 mt-2 m-0">Tap a die to hold / unhold it</p>
                )}
            </section>

            {/* Action buttons */}
            <section className="flex gap-3 flex-wrap">
                <button
                    disabled={!canRoll || acting}
                    onClick={() => { setIsRolling(true); setTimeout(() => setIsRolling(false), 400); act(() => turnsApi.roll(id)) }}
                    className="px-5 py-2.5 border-0 rounded-md font-karla font-bold text-sm cursor-pointer bg-bankroll-primary text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Roll
                </button>
                <button
                    disabled={!canBank || acting}
                    onClick={() => act(() => turnsApi.bankRolls(id))}
                    className="px-5 py-2.5 border-0 rounded-md font-karla font-bold text-sm cursor-pointer bg-bankroll-teal text-white hover:opacity-90 disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                    Bank rolls
                </button>
                <button
                    onClick={openScoresheet}
                    className="px-5 py-2.5 border-0 rounded-md font-karla font-bold text-sm cursor-pointer bg-bankroll-header text-white hover:opacity-90"
                >
                    Scoresheet
                </button>
            </section>

            {/* Players scoreboard */}
            {game?.players && game.players.length > 1 && (
                <section>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Players</h3>
                    <div className="flex gap-2 flex-wrap">
                        {game.players.map(p => (
                            <div
                                key={p.player_id}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${
                                    p.player_id === game.current_player_id
                                        ? 'bg-bankroll-primary text-white border-bankroll-primary'
                                        : 'bg-gray-800 text-gray-400 border-gray-700'
                                }`}
                            >
                                {p.player_id === game.current_player_id && <span>▶</span>}
                                {p.username}
                                {p.player_id === user?.id && ' (you)'}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Scoresheet */}
            <section>
                <h3 className="text-xs font-bold text-bankroll-on-teal uppercase tracking-wide mb-2">
                    {isMyTurn
                        ? (forcedSelection ? 'Choose a category to zero out' : 'Score a category')
                        : `${myPlayer?.username ?? 'Your'} scoresheet`}
                </h3>
                <div className="grid grid-cols-2 gap-1.5">
                    {Object.entries(CATEGORY_NAMES).map(([idStr, name]) => {
                        const catId   = parseInt(idStr, 10)
                        const isFilled = catId in filledCategories
                        const isMet    = catId in metCategories
                        const score    = isFilled ? filledCategories[catId] : (isMet ? metCategories[catId] : null)
                        const canClick = isMyTurn && !isFilled && (isMet || forcedSelection)

                        return (
                            <button
                                key={catId}
                                disabled={!canClick || acting}
                                onClick={canClick ? () => act(() => turnsApi.scoreCategory(id, catId)) : undefined}
                                onContextMenu={e => handleCategoryContextMenu(e, catId)}
                                onTouchStart={e => handleCategoryTouchStart(e, catId)}
                                onTouchEnd={cancelLongPress}
                                onTouchMove={cancelLongPress}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${
                                    isFilled
                                        ? 'bg-bankroll-met text-white border-bankroll-met cursor-default'
                                        : isMet
                                        ? 'bg-bankroll-lavender text-bankroll-primary border-bankroll-primary cursor-pointer hover:bg-bankroll-primary hover:text-white'
                                        : 'bg-gray-800 text-bankroll-category-unmet border-gray-700 cursor-default'
                                } disabled:cursor-not-allowed`}
                            >
                                <span>{name}</span>
                                <span>{score !== null ? score : '—'}</span>
                            </button>
                        )
                    })}
                </div>
                {isMyTurn && !canScore && !forcedSelection && dice.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2 m-0">No categories met yet — keep rolling</p>
                )}
            </section>

            <button
                className="self-start mt-2 px-4 py-2 border border-bankroll-on-teal rounded-md text-bankroll-on-teal text-sm font-karla cursor-pointer hover:opacity-70 bg-transparent"
                onClick={() => navigate('/lobby')}
            >
                ← Back to Lobby
            </button>
        </main>
        </div>

        {contextMenu && (
            <div
                className="fixed z-[400] bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 w-52 pointer-events-none"
                style={{ top: contextMenu.y, left: contextMenu.x }}
            >
                <p className="font-bold text-bankroll-primary text-xs mb-1 m-0">{CATEGORY_NAMES[contextMenu.catId]}</p>
                <p className="text-gray-300 text-xs m-0 leading-snug">{CATEGORY_HINTS[contextMenu.catId]}</p>
            </div>
        )}

        {showScoresheet && scoresheet && (
            <div className="fixed inset-0 z-[200] bg-bankroll-navy overflow-auto pt-[60px] sm:pt-20">
                <div className="page-main">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-gray-100 m-0">Scoresheet</h2>
                        <button
                            onClick={() => setShowScoresheet(false)}
                            className="px-4 py-2 bg-bankroll-primary text-white border-0 rounded-md font-karla font-bold text-sm cursor-pointer hover:opacity-90"
                        >
                            Back to Game
                        </button>
                    </div>

                    <div className="overflow-x-auto rounded-xl">
                        <table
                            className="w-full border-collapse text-sm"
                            style={{ gridTemplateColumns: `repeat(${Math.min(1 + game.players.length, 7)}, minmax(60px, 1fr))` }}
                        >
                            <thead>
                                <tr className="border-b border-gray-700">
                                    <th className="text-left px-3 py-2.5 text-gray-400 font-bold uppercase text-xs tracking-wide whitespace-nowrap">Category</th>
                                    {game.players.map(p => (
                                        <th key={p.player_id} className="text-center px-3 py-2.5 text-gray-200 font-bold text-xs whitespace-nowrap">
                                            {p.username}{p.player_id === user?.id ? ' (you)' : ''}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(CATEGORY_NAMES).map(([idStr, name], i) => {
                                    const catId = parseInt(idStr, 10)
                                    const rowBg = i % 2 === 0 ? 'bg-gray-900' : ''
                                    return (
                                        <tr key={catId} className="border-b border-gray-800">
                                            <td className={`px-3 py-2 text-gray-300 font-bold whitespace-nowrap ${rowBg}`}>{name}</td>
                                            {game.players.map(p => {
                                                const score = scoresheet.scores[p.player_id]?.[catId]
                                                return (
                                                    <td key={p.player_id} className={`text-center px-3 py-2 font-bold whitespace-nowrap ${rowBg} ${score !== undefined ? 'text-bankroll-teal' : 'text-gray-600'}`}>
                                                        {score !== undefined ? score : '—'}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                                <tr className="border-t-2 border-gray-600">
                                    <td className="px-3 py-2.5 text-gray-400 font-bold uppercase text-xs tracking-wide whitespace-nowrap">Total</td>
                                    {game.players.map(p => {
                                        const total = Object.values(scoresheet.scores[p.player_id] ?? {}).reduce((s, v) => s + v, 0)
                                        return (
                                            <td key={p.player_id} className="text-center px-3 py-2.5 font-bold text-bankroll-primary whitespace-nowrap">
                                                {total}
                                            </td>
                                        )
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}
        </>
    )
}
